/* For date series */

import { ChartOptions } from 'plume-ui';
import { alarmCategoryColors } from '../../../utils/AlarmUtils';
import i18n from '../../../utils/i18n';
import { GetCriteriaNameFn } from '../../Monitor/hooks/useCriteriaNames';
import { HealthStatsType } from './healthBreakdownProc';

export type TimelineDataType = {
  ts: string;
  average_health_score: number;
};

export type TimelineChartDataType = {
  xAxisLabel: string;
  typicalScore: number;
  partnerScore: number;
};

export type TimelineGroupsType = {
  label: string;
  stroke: string;
  key: string;
  strokeWidth: number;
};

export const procTimeline = (
  typicalData: TimelineDataType[],
  partnerData: TimelineDataType[],
  timestampFormatter: (timestamp: string) => string,
): TimelineChartDataType[] => {
  let typicalTimelineData = [...typicalData];
  let partnerTimelineData = [...partnerData];
  const timeLineData: TimelineChartDataType[] = [];
  typicalTimelineData = typicalTimelineData.sort(
    (a: TimelineDataType, b: TimelineDataType) =>
      a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0,
  );
  partnerTimelineData = partnerTimelineData.sort(
    (a: TimelineDataType, b: TimelineDataType) =>
      a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0,
  );

  typicalTimelineData.forEach((dat: TimelineDataType) => {
    const partnerScore = partnerTimelineData.find((item) => item.ts === dat.ts)
      ?.average_health_score;
    if (dat?.average_health_score && partnerScore) {
      timeLineData.push({
        xAxisLabel: timestampFormatter(dat.ts),
        typicalScore: dat?.average_health_score,
        partnerScore: partnerScore,
      });
    }
  });
  return timeLineData;
};

export const getTimelineGroups = (): TimelineGroupsType[] => [
  {
    label: i18n.t('healthSummary.breakdown.myNetwork'),
    stroke: '#575BCF',
    key: 'partnerScore',
    strokeWidth: 2,
  },
  {
    label: i18n.t('healthSummary.breakdown.typNetwork'),
    stroke: '#81BB85',
    key: 'typicalScore',
    strokeWidth: 2,
  },
];

export const getIssueStackGroup = (
  getCriteriaName: GetCriteriaNameFn,
): ChartOptions.ComposedChartGroup[] =>
  [
    {
      color: '#575BCF',
      key: 'environment',
      stroke: '#575BCF',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#567C7C',
      key: 'internet_connection',
      stroke: '#567C7C',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#00BEDB',
      key: 'stability',
      stroke: '#00BEDB',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#C9C572',
      key: 'network_speed',
      stroke: '#C9C572',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#81BB85',
      key: 'pod_qoe',
      stroke: '#81BB85',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#855670',
      key: 'device_qoe',
      stroke: '#855670',
      type: ChartOptions.ChartType.BAR,
    },
    {
      color: '#6327AA',
      key: 'wifi_coverage',
      stroke: '#6327AA',
      type: ChartOptions.ChartType.BAR,
    },
  ].map((group) => ({
    ...group,
    label: getCriteriaName(group.key),
  }));

export const procStack = (issueData: HealthStatsType): issueMapType[] => {
  const keys = Object.keys(issueData);
  const locationCount = issueData?.location_count;

  const issueMap: issueMapType[] = alarmCategoryColors.map((color) => {
    return {
      fill: color.fill,
      type: color.type,
      id: color.id,
    };
  });

  keys
    ?.filter((key: string) => key.includes('category'))
    .forEach((key: string) => {
      const keyId = key.split('_category_location_count')[0];

      // @ts-ignore
      const keyVal = issueData[key];

      const found = issueMap.find((issue: issueMapType) => issue.id === keyId);

      if (found) {
        found.count = keyVal;
        found.norm =
          found.count && locationCount
            ? found.count * locationCount
            : found.count;
      }
    });
  return issueMap;
};

export type StackDataType = {
  name: string;
  values: number[];
};

type issueMapType = {
  count?: number;
  fill?: string;
  id: string;
  norm?: number;
  type: string;
};

export const procStacks = (
  dailyTypicalIssues: HealthStatsType,
  dailyPartnerIssues: HealthStatsType,
): StackDataType[] => {
  let stackData: StackDataType[] = [
    { name: i18n.t('healthSummary.breakdown.myNetwork'), values: [] },
    { name: i18n.t('healthSummary.breakdown.typNetwork'), values: [] },
  ];
  if (dailyTypicalIssues && dailyPartnerIssues) {
    const typicalIssueMap: issueMapType[] = procStack(dailyTypicalIssues);
    const partnerIssueMap: issueMapType[] = procStack(dailyPartnerIssues);

    if (partnerIssueMap) {
      partnerIssueMap.forEach((iss: issueMapType) => {
        stackData[0].values.push(iss.norm ? iss.norm : 0);
      });
    }

    if (typicalIssueMap) {
      typicalIssueMap.forEach((iss: issueMapType) => {
        stackData[1].values.push(iss.norm ? iss.norm : 0);
      });
    }
  }
  return stackData;
};
